import React, { useEffect, useState } from "react";
import { Box, IconButton, Modal, Stack, Typography } from "@mui/material";
import {
  Close,
  DeleteOutline,
  NavigateBefore,
  NavigateNext,
  RotateLeft,
  RotateRight,
  ZoomIn,
  ZoomOut,
} from "@mui/icons-material";
import { colors } from "../../theme/theme";

const MediaViewerModal = ({
  open,
  onClose,
  src,
  images,
  title,
  rotatable = false,
  onDelete,
}) => {
  const gallery = images?.length ? images : src ? [src] : [];

  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    if (open) {
      setIndex(0);
      setZoom(1);
      setRotation(0);
    }
  }, [open]);

  const handleClose = () => {
    setZoom(1);
    setRotation(0);
    onClose();
  };

  const zoomIn = () => setZoom((z) => Math.min(z + 0.25, 3));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.25, 0.5));
  const rotateLeft = () => setRotation((r) => r - 90);
  const rotateRight = () => setRotation((r) => r + 90);

  const showPrev = () => {
    setZoom(1);
    setRotation(0);
    setIndex((i) => (i === 0 ? gallery.length - 1 : i - 1));
  };
  const showNext = () => {
    setZoom(1);
    setRotation(0);
    setIndex((i) => (i === gallery.length - 1 ? 0 : i + 1));
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.9)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 2 }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontFamily: "Mulish-SemiBold",
              fontSize: "16px",
            }}
          >
            {title}
            {gallery.length > 1 ? ` (${index + 1}/${gallery.length})` : ""}
          </Typography>
          <Stack direction="row" spacing={1}>
            <IconButton onClick={zoomOut}>
              <ZoomOut sx={{ color: "#fff" }} />
            </IconButton>
            <IconButton onClick={zoomIn}>
              <ZoomIn sx={{ color: "#fff" }} />
            </IconButton>
            {rotatable && (
              <>
                <IconButton onClick={rotateLeft}>
                  <RotateLeft sx={{ color: "#fff" }} />
                </IconButton>
                <IconButton onClick={rotateRight}>
                  <RotateRight sx={{ color: "#fff" }} />
                </IconButton>
              </>
            )}
            {onDelete && (
              <IconButton onClick={onDelete}>
                <DeleteOutline sx={{ color: colors.secondary }} />
              </IconButton>
            )}
            <IconButton onClick={handleClose}>
              <Close sx={{ color: "#fff" }} />
            </IconButton>
          </Stack>
        </Stack>

        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "auto",
            p: 3,
            position: "relative",
          }}
        >
          {gallery.length > 1 && (
            <IconButton
              onClick={showPrev}
              sx={{
                position: "absolute",
                left: 8,
                backgroundColor: "rgba(255,255,255,0.1)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
              }}
            >
              <NavigateBefore sx={{ color: "#fff" }} />
            </IconButton>
          )}

          <Box
            component="img"
            src={gallery[index]}
            alt={title}
            sx={{
              maxWidth: "90%",
              maxHeight: "80vh",
              transform: `scale(${zoom}) rotate(${rotation}deg)`,
              transition: "transform 0.2s ease",
              objectFit: "contain",
            }}
          />

          {gallery.length > 1 && (
            <IconButton
              onClick={showNext}
              sx={{
                position: "absolute",
                right: 8,
                backgroundColor: "rgba(255,255,255,0.1)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
              }}
            >
              <NavigateNext sx={{ color: "#fff" }} />
            </IconButton>
          )}
        </Box>

        {gallery.length > 1 && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ p: 2, overflowX: "auto" }}
            justifyContent="center"
          >
            {gallery.map((image, i) => (
              <Box
                key={i}
                component="img"
                src={image}
                alt={`thumb-${i}`}
                onClick={() => {
                  setZoom(1);
                  setRotation(0);
                  setIndex(i);
                }}
                sx={{
                  width: "56px",
                  height: "56px",
                  objectFit: "cover",
                  borderRadius: "6px",
                  cursor: "pointer",
                  border:
                    i === index
                      ? `2px solid ${colors.secondary}`
                      : "2px solid transparent",
                  opacity: i === index ? 1 : 0.6,
                }}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Modal>
  );
};

export default MediaViewerModal;
